import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, money, fmtDate, PageHead, PrimaryBtn, FinanceEmpty, StatusBadge, exportExcel, PrintStyle, printArea, errMsg, Field, Modal, inputCls } from './shared';
import Icon from '../../../components/dashboard/DashboardIcons';

const MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const PAY_METHODS = { cash: 'نقدي', bank_transfer: 'تحويل بنكي', credit_card: 'بطاقة', check: 'شيك' };
const _nextMonth = (m, y) => (m === 12 ? [1, y + 1] : [m + 1, y]);

export default function PayrollPage() {
  const [tab, setTab] = useState('payroll');
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  return (
    <div className="mx-auto max-w-6xl space-y-4" data-testid="payroll-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg print:hidden">{toast}</div>}
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1.5 print:hidden" data-testid="payroll-sub-tabs">
        {[{ key: 'payroll', label: 'كشف الرواتب' }, { key: 'advances', label: 'سلف الموظفين' }].map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`payroll-sub-tab-${t.key}`}
            className={`shrink-0 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${tab === t.key ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'payroll' ? <PayrollTab notify={notify} /> : <AdvancesTab notify={notify} />}
    </div>
  );
}

const STATUS_AR = { pending: 'مستحق', partial: 'مدفوع جزئياً', paid: 'مدفوع', overdue: 'متأخر' };

function PayrollTab({ notify }) {
  const now = new Date();
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'arrears'
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payModal, setPayModal] = useState(null); // record being paid
  const [historyModal, setHistoryModal] = useState(null); // record whose history is shown
  const [payForm, setPayForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), payment_method: 'cash', reference: '', notes: '' });
  const [payErr, setPayErr] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const url = viewMode === 'arrears' ? `${API}/payroll/outstanding` : `${API}/payroll?month=${month}&year=${year}`;
      setRecords((await axios.get(url, auth())).data || []);
    }
    catch (e) { setError(e.response?.status === 403 ? 'لا تملك صلاحية الوصول للرواتب' : 'تعذر التحميل'); }
    setLoading(false);
  }, [month, year, viewMode]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    try {
      const res = await axios.post(`${API}/payroll/generate`, { month, year }, auth());
      notify(res.data.created > 0 ? `تم توليد ${res.data.created} سجل راتب من وحدة الموظفين` : 'الكشف مولّد مسبقاً لهذا الشهر');
      load();
    } catch (e) { notify(errMsg(e, 'فشل التوليد')); }
  };

  const openPay = (r) => {
    setPayForm({ amount: r.remaining_amount, payment_date: new Date().toISOString().slice(0, 10), payment_method: 'cash', reference: '', notes: '' });
    setPayErr(null); setPayModal(r);
  };

  const submitPay = async (e) => {
    e.preventDefault(); setPayErr(null); setBusy(true);
    try {
      await axios.post(`${API}/payroll/${payModal.id}/payments`, {
        amount: parseFloat(payForm.amount), payment_date: payForm.payment_date,
        payment_method: payForm.payment_method, reference: payForm.reference || null, notes: payForm.notes || null,
      }, auth());
      notify('تم تسجيل الدفعة بنجاح'); setPayModal(null); load();
    } catch (e2) { setPayErr(errMsg(e2, 'فشل تسجيل الدفعة')); }
    setBusy(false);
  };

  const openHistory = async (r) => {
    setHistoryModal(r);
    try { setHistory((await axios.get(`${API}/payroll/${r.id}/payments`, auth())).data || []); }
    catch { setHistory([]); }
  };

  const applyAdvance = async (r) => {
    try {
      await axios.post(`${API}/payroll/${r.id}/apply-advance-deduction`, { advance_id: r.pending_advance_deduction.advance_id }, auth());
      notify('تم تطبيق استقطاع السلفة على الكشف'); load();
    } catch (e) { notify(errMsg(e, 'فشل تطبيق الاستقطاع')); }
  };

  const applyAdvanceThenReopen = async (r) => {
    try {
      const res = await axios.post(`${API}/payroll/${r.id}/apply-advance-deduction`, { advance_id: r.pending_advance_deduction.advance_id }, auth());
      notify('تم تطبيق استقطاع السلفة — تم تحديث الصافي والمتبقي');
      setPayModal(res.data);
      setPayForm({ ...payForm, amount: res.data.remaining_amount });
      load();
    } catch (e) { notify(errMsg(e, 'فشل تطبيق الاستقطاع')); }
  };

  if (error) return <div className="rounded-xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-600" data-testid="payroll-error">{error}</div>;
  const totalNet = records.reduce((s, r) => s + r.net_salary, 0);
  const totalPaid = records.reduce((s, r) => s + (r.paid_amount || 0), 0);

  return (
    <div className="space-y-4 print-area" data-testid="payroll-tab">
      <PrintStyle />
      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1.5 print:hidden" data-testid="payroll-view-toggle">
        <button type="button" onClick={() => setViewMode('month')} data-testid="payroll-view-month-btn"
          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${viewMode === 'month' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>شهر محدد</button>
        <button type="button" onClick={() => setViewMode('arrears')} data-testid="payroll-view-arrears-btn"
          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${viewMode === 'arrears' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>كل المتأخرات</button>
      </div>
      <PageHead title={viewMode === 'arrears' ? 'كل المتأخرات' : 'كشف الرواتب'}
        desc={viewMode === 'arrears' ? 'كل الرواتب غير المدفوعة بالكامل من كل الشهور السابقة في مكان واحد' : `كشف رواتب ${MONTHS[month - 1]} ${year} — مرتبط بوحدة الموظفين`}
        action={<div className="flex flex-wrap gap-2 print:hidden">
          {viewMode === 'month' && (
            <>
              <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm" data-testid="payroll-month-select">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm" data-testid="payroll-year-select">
                {[year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
          <button onClick={() => exportExcel(records.map((r) => ({ 'الموظف': r.employee_name, 'الرقم': r.employee_number || '', 'الشهر': `${MONTHS[r.month - 1]} ${r.year}`, 'الأساسي': r.basic_salary, 'البدلات': r.allowances, 'الاستقطاعات': r.deductions, 'الصافي': r.net_salary, 'المدفوع': r.paid_amount, 'المتبقي': r.remaining_amount, 'الحالة': STATUS_AR[r.status] || r.status })), viewMode === 'arrears' ? 'المتأخرات' : `رواتب-${month}-${year}`)}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600" data-testid="payroll-export-btn"><Icon name="chart" className="w-4 h-4" /> Excel</button>
          <button onClick={printArea} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600" data-testid="payroll-print-btn"><Icon name="document" className="w-4 h-4" /> طباعة</button>
          {viewMode === 'month' && <PrimaryBtn onClick={generate} data-testid="payroll-generate-btn"><Icon name="plus" className="w-4 h-4" /> توليد الكشف</PrimaryBtn>}
        </div>} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">{viewMode === 'arrears' ? 'عدد السجلات المتأخرة' : 'عدد الموظفين بالكشف'}</div><div className="text-xl font-bold text-slate-900" data-testid="payroll-count">{records.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي الصافي</div><div className="text-xl font-bold text-slate-900" dir="rtl" data-testid="payroll-total">{money(totalNet)}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">تم صرفه</div><div className="text-xl font-bold text-teal-700" dir="rtl" data-testid="payroll-paid-total">{money(totalPaid)}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">المتبقي</div><div className="text-xl font-bold text-amber-700" dir="rtl" data-testid="payroll-remaining-total">{money(totalNet - totalPaid)}</div></div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : records.length === 0 ? <div className="p-6"><FinanceEmpty text={viewMode === 'arrears' ? 'لا توجد رواتب متأخرة — كل الكشوف مدفوعة بالكامل' : 'لا يوجد كشف رواتب لهذا الشهر — اضغط «توليد الكشف» لإنشائه من الموظفين النشطين'} /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">الموظف</th>
                  {viewMode === 'arrears' && <th className="px-4 py-2.5 font-semibold">الشهر</th>}
                  <th className="hidden px-4 py-2.5 font-semibold md:table-cell">الأساسي</th>
                  <th className="hidden px-4 py-2.5 font-semibold md:table-cell">البدلات</th><th className="hidden px-4 py-2.5 font-semibold lg:table-cell">الاستقطاعات</th>
                  <th className="px-4 py-2.5 font-semibold">الصافي</th><th className="hidden px-4 py-2.5 font-semibold sm:table-cell">المتبقي</th>
                  <th className="px-4 py-2.5 font-semibold">الحالة</th><th className="px-4 py-2.5 font-semibold print:hidden">إجراء</th>
                </tr></thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50" data-testid="payroll-row">
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-slate-800">{r.employee_name}</div>
                        <div className="text-xs text-slate-400" dir="ltr">{r.employee_number || ''}</div>
                        {r.pending_advance_deduction && (
                          <div className="mt-1 flex items-center gap-1.5 rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-700 print:hidden" data-testid="payroll-advance-suggestion">
                            <span>يوجد قسط سلفة مجدول ({money(r.pending_advance_deduction.amount)})</span>
                            <button onClick={() => applyAdvance(r)} className="font-bold underline" data-testid="payroll-apply-advance-btn">تطبيق</button>
                          </div>
                        )}
                      </td>
                      {viewMode === 'arrears' && <td className="px-4 py-2.5 text-slate-600" data-testid="payroll-row-month">{MONTHS[r.month - 1]} {r.year}</td>}
                      <td className="hidden px-4 py-2.5 text-slate-600 md:table-cell" dir="rtl">{money(r.basic_salary)}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 md:table-cell" dir="rtl">{money(r.allowances)}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 lg:table-cell" dir="rtl">{money(r.deductions)}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-900" dir="rtl">{money(r.net_salary)}</td>
                      <td className="hidden px-4 py-2.5 text-amber-700 sm:table-cell" dir="rtl" data-testid="payroll-row-remaining">{money(r.remaining_amount)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-2.5 print:hidden">
                        <div className="flex items-center gap-2">
                          {r.status !== 'paid' && <button onClick={() => openPay(r)} className="rounded-lg bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100" data-testid="payroll-pay-btn">تسجيل دفعة</button>}
                          <button onClick={() => openHistory(r)} className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="payroll-history-btn">السجل</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Modal open={!!payModal} title={`تسجيل دفعة راتب — ${payModal?.employee_name || ''}`} onClose={() => setPayModal(null)}>
        {payModal && (
          <form onSubmit={submitPay} className="space-y-3" data-testid="payroll-pay-form">
            {payErr && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="payroll-pay-error">{payErr}</div>}
            {payModal.pending_advance_deduction && (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="payroll-pay-advance-warning">
                <span>تنبيه: يوجد قسط سلفة مجدول هذا الشهر ({money(payModal.pending_advance_deduction.amount)}) لم يُطبَّق بعد — إن سجّلت الدفعة الآن فلن تتمكن من تطبيقه على هذا الكشف لاحقاً.</span>
                <button type="button" onClick={() => applyAdvanceThenReopen(payModal)} className="shrink-0 whitespace-nowrap rounded-lg bg-amber-600 px-2.5 py-1 font-bold text-white hover:bg-amber-700" data-testid="payroll-pay-apply-advance-btn">طبّق السلفة أولاً</button>
              </div>
            )}
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">الصافي: {money(payModal.net_salary)} — المدفوع: {money(payModal.paid_amount)} — المتبقي: <b className="text-amber-700">{money(payModal.remaining_amount)}</b></div>
            <Field label="المبلغ المدفوع" required>
              <input type="number" step="0.01" min="0.01" max={payModal.remaining_amount} className={inputCls} dir="ltr" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payroll-pay-amount" />
            </Field>
            <Field label="تاريخ الدفع" required>
              <input type="date" className={inputCls} value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} data-testid="payroll-pay-date" />
            </Field>
            <Field label="طريقة الدفع" required>
              <select className={inputCls} value={payForm.payment_method} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} data-testid="payroll-pay-method">
                {Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="مرجع الدفعة (اختياري)"><input className={inputCls} dir="ltr" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="payroll-pay-reference" /></Field>
            <Field label="ملاحظات (اختياري)"><textarea className={inputCls} rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="payroll-pay-notes" /></Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setPayModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <PrimaryBtn type="submit" disabled={busy} data-testid="payroll-pay-submit">{busy ? 'جارٍ الحفظ...' : 'تسجيل الدفعة'}</PrimaryBtn>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!historyModal} title={`سجل دفعات — ${historyModal?.employee_name || ''}`} onClose={() => setHistoryModal(null)} wide>
        {history.length === 0 ? <FinanceEmpty text="لا توجد دفعات مسجلة على هذا الكشف بعد" /> : (
          <div className="overflow-x-auto" data-testid="payroll-history-list">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                <th className="px-3 py-2 font-semibold">التاريخ</th><th className="px-3 py-2 font-semibold">المبلغ</th><th className="px-3 py-2 font-semibold">الطريقة</th><th className="px-3 py-2 font-semibold">المرجع</th>
              </tr></thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50" data-testid="payroll-history-row">
                    <td className="px-3 py-2 text-slate-600">{fmtDate(p.payment_date)}</td>
                    <td className="px-3 py-2 font-bold text-slate-900" dir="rtl">{money(p.amount)}</td>
                    <td className="px-3 py-2 text-slate-600">{PAY_METHODS[p.payment_method] || p.payment_method}</td>
                    <td className="px-3 py-2 text-slate-500" dir="ltr">{p.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}

const STATUS_LABELS_ADV = { active: 'نشطة', settled: 'مسدَّدة', cancelled: 'ملغاة' };

function AdvancesTab({ notify }) {
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModal, setFormModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const now = new Date();
  const [form, setForm] = useState({
    employee_id: '', amount: '', advance_date: now.toISOString().slice(0, 10), reason: '', payment_method: 'cash',
    repayment_type: 'single_month', target_month: now.getMonth() + 1, target_year: now.getFullYear(),
    installment_count: 2, installment_amount: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [advRes, empRes] = await Promise.all([
        axios.get(`${API}/employee-advances`, auth()),
        axios.get(`${API}/employees`, auth()),
      ]);
      setAdvances(advRes.data || []); setEmployees((empRes.data || []).filter((e) => e.is_active));
    } catch { setAdvances([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    setForm({ employee_id: employees[0]?.id || '', amount: '', advance_date: now.toISOString().slice(0, 10), reason: '', payment_method: 'cash', repayment_type: 'single_month', target_month: now.getMonth() + 1, target_year: now.getFullYear(), installment_count: 2, installment_amount: '' });
    setErr(null); setFormModal(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const payload = {
        employee_id: form.employee_id, amount: parseFloat(form.amount), advance_date: form.advance_date,
        reason: form.reason || null, payment_method: form.payment_method, repayment_type: form.repayment_type,
        target_month: parseInt(form.target_month), target_year: parseInt(form.target_year),
      };
      if (form.repayment_type === 'installments') {
        payload.installment_count = parseInt(form.installment_count);
        payload.installment_amount = parseFloat(form.installment_amount);
      }
      await axios.post(`${API}/employee-advances`, payload, auth());
      notify('تم تسجيل السلفة وصرفها كمصروف'); setFormModal(false); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل تسجيل السلفة')); }
    setBusy(false);
  };

  const cancel = async () => {
    try { await axios.post(`${API}/employee-advances/${cancelTarget.id}/cancel`, {}, auth()); notify('تم إلغاء السلفة'); setCancelTarget(null); load(); }
    catch (e) { notify(errMsg(e, 'تعذر إلغاء السلفة')); setCancelTarget(null); }
  };

  const [rescheduleTarget, setRescheduleTarget] = useState(null); // { advance, entry }
  const [rescheduleForm, setRescheduleForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [rescheduleErr, setRescheduleErr] = useState(null);

  const openReschedule = (advance, entry) => {
    const [nm, ny] = _nextMonth(entry.month, entry.year);
    setRescheduleForm({ month: nm, year: ny }); setRescheduleErr(null); setRescheduleTarget({ advance, entry });
  };

  const submitReschedule = async (e) => {
    e.preventDefault(); setRescheduleErr(null);
    try {
      await axios.post(`${API}/employee-advances/${rescheduleTarget.advance.id}/reschedule`, {
        old_month: rescheduleTarget.entry.month, old_year: rescheduleTarget.entry.year,
        new_month: parseInt(rescheduleForm.month), new_year: parseInt(rescheduleForm.year),
      }, auth());
      notify('تم نقل القسط إلى الشهر الجديد'); setRescheduleTarget(null); load();
    } catch (e2) { setRescheduleErr(errMsg(e2, 'فشل نقل القسط')); }
  };

  return (
    <div className="space-y-4" data-testid="advances-tab">
      <PageHead title="سلف الموظفين" desc="تُسجَّل السلفة كمصروف فعلي فور صرفها، وتُخصم لاحقاً من الراتب كاقتراح تؤكده"
        action={<PrimaryBtn onClick={openForm} data-testid="advance-add-btn"><Icon name="plus" className="w-4 h-4" /> سلفة جديدة</PrimaryBtn>} />

      <div className="rounded-xl border border-slate-200 bg-white">
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : advances.length === 0 ? <div className="p-6"><FinanceEmpty text="لا توجد سلف مسجّلة" /></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">الموظف</th><th className="px-4 py-2.5 font-semibold">المبلغ</th>
                  <th className="hidden px-4 py-2.5 font-semibold sm:table-cell">تاريخ الصرف</th>
                  <th className="px-4 py-2.5 font-semibold">المتبقي</th><th className="px-4 py-2.5 font-semibold">الحالة</th>
                  <th className="hidden px-4 py-2.5 font-semibold md:table-cell">السداد</th>
                  <th className="px-4 py-2.5 font-semibold">إجراء</th>
                </tr></thead>
                <tbody>
                  {advances.map((a) => (
                    <tr key={a.id} className="border-b border-slate-50" data-testid="advance-row">
                      <td className="px-4 py-2.5 font-semibold text-slate-800">{a.employee_name}</td>
                      <td className="px-4 py-2.5 font-bold text-slate-900" dir="rtl">{money(a.amount)}</td>
                      <td className="hidden px-4 py-2.5 text-slate-600 sm:table-cell">{fmtDate(a.advance_date)}</td>
                      <td className="px-4 py-2.5 text-amber-700" dir="rtl" data-testid="advance-remaining">{money(a.remaining_balance)}</td>
                      <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.status === 'settled' ? 'bg-teal-50 text-teal-700' : a.status === 'cancelled' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`}>{STATUS_LABELS_ADV[a.status] || a.status}</span></td>
                      <td className="hidden px-4 py-2.5 text-xs text-slate-500 md:table-cell">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {a.schedule.map((s, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-0.5">
                              {MONTHS[s.month - 1]}/{s.year} ({s.applied ? '✓ طُبِّق' : 'لم يُطبَّق'})
                              {!s.applied && a.status === 'active' && (
                                <button type="button" onClick={() => openReschedule(a, s)} className="font-bold text-teal-600 underline" data-testid="advance-reschedule-btn">نقل</button>
                              )}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {a.status === 'active' && a.remaining_balance >= a.amount - 0.01 && (
                          <button onClick={() => setCancelTarget(a)} className="rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid="advance-cancel-btn">إلغاء</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Modal open={formModal} title="سلفة جديدة" onClose={() => setFormModal(false)} wide>
        <form onSubmit={submit} className="space-y-3" data-testid="advance-form">
          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="advance-form-error">{err}</div>}
          <Field label="الموظف" required>
            <select className={inputCls} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="advance-employee-select">
              <option value="">اختر موظفاً</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} — {e.position}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="قيمة السلفة" required><input type="number" step="0.01" min="0.01" className={inputCls} dir="ltr" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="advance-amount" /></Field>
            <Field label="تاريخ الصرف" required><input type="date" className={inputCls} value={form.advance_date} onChange={(e) => setForm({ ...form, advance_date: e.target.value })} data-testid="advance-date" /></Field>
          </div>
          <Field label="طريقة الصرف" required>
            <select className={inputCls} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="advance-payment-method">
              {Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="السبب (اختياري)"><input className={inputCls} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="advance-reason" /></Field>

          <Field label="طريقة الخصم من الراتب" required>
            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 text-sm"><input type="radio" checked={form.repayment_type === 'single_month'} onChange={() => setForm({ ...form, repayment_type: 'single_month' })} data-testid="advance-repay-single" /> دفعة واحدة كاملة</label>
              <label className="flex items-center gap-1.5 text-sm"><input type="radio" checked={form.repayment_type === 'installments'} onChange={() => setForm({ ...form, repayment_type: 'installments' })} data-testid="advance-repay-installments" /> على دفعات</label>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={form.repayment_type === 'single_month' ? 'شهر الخصم' : 'شهر بداية الخصم'} required>
              <select className={inputCls} value={form.target_month} onChange={(e) => setForm({ ...form, target_month: e.target.value })} data-testid="advance-target-month">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </Field>
            <Field label="السنة" required><input type="number" className={inputCls} dir="ltr" value={form.target_year} onChange={(e) => setForm({ ...form, target_year: e.target.value })} data-testid="advance-target-year" /></Field>
          </div>
          {form.repayment_type === 'installments' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="عدد الدفعات" required><input type="number" min="1" max="36" className={inputCls} dir="ltr" value={form.installment_count} onChange={(e) => setForm({ ...form, installment_count: e.target.value })} data-testid="advance-installment-count" /></Field>
              <Field label="قيمة كل دفعة" required><input type="number" step="0.01" min="0.01" className={inputCls} dir="ltr" value={form.installment_amount} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} data-testid="advance-installment-amount" /></Field>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFormModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={busy} data-testid="advance-form-submit">{busy ? 'جارٍ الحفظ...' : 'صرف السلفة'}</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={!!cancelTarget} title="إلغاء السلفة" onClose={() => setCancelTarget(null)}>
        <p className="text-sm text-slate-600">سيتم إلغاء سلفة «{cancelTarget?.employee_name}» بقيمة {money(cancelTarget?.amount || 0)} وإلغاء المصروف المرتبط بها. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setCancelTarget(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={cancel} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="advance-confirm-cancel">تأكيد الإلغاء</button>
        </div>
      </Modal>

      <Modal open={!!rescheduleTarget} title={`نقل قسط سلفة — ${rescheduleTarget?.advance?.employee_name || ''}`} onClose={() => setRescheduleTarget(null)}>
        {rescheduleTarget && (
          <form onSubmit={submitReschedule} className="space-y-3" data-testid="advance-reschedule-form">
            {rescheduleErr && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="advance-reschedule-error">{rescheduleErr}</div>}
            <p className="text-sm text-slate-600">نقل قسط {money(rescheduleTarget.entry.amount)} المجدول حالياً لشهر {MONTHS[rescheduleTarget.entry.month - 1]} {rescheduleTarget.entry.year} إلى:</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="الشهر الجديد" required>
                <select className={inputCls} value={rescheduleForm.month} onChange={(e) => setRescheduleForm({ ...rescheduleForm, month: e.target.value })} data-testid="advance-reschedule-month">
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </Field>
              <Field label="السنة" required><input type="number" className={inputCls} dir="ltr" value={rescheduleForm.year} onChange={(e) => setRescheduleForm({ ...rescheduleForm, year: e.target.value })} data-testid="advance-reschedule-year" /></Field>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRescheduleTarget(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
              <PrimaryBtn type="submit" data-testid="advance-reschedule-submit">نقل القسط</PrimaryBtn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
